import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { CrmUser } from '../../types';
import { getCrmUsers } from '../../services/crmService';
import { useOrganization } from '../../contexts/OrganizationContext';
import { ErrorHandler } from '../../core/errors/ErrorHandler';

// Centralized DI
import {
    createUserUseCase,
    updateUserUseCase,
    deleteUserUseCase
} from '../../infrastructure/di/crmContainer';

interface UsersContextType {
    crmUsers: CrmUser[];
    isLoading: boolean;

    // Actions
    saveCrmUser: (userToSave: CrmUser) => Promise<void>;
    deleteCrmUser: (userId: string) => Promise<void>;
    refreshUsers: () => Promise<void>;
}

const UsersContext = createContext<UsersContextType | undefined>(undefined);

export const UsersProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { currentOrganization } = useOrganization();
    const [crmUsers, setCrmUsers] = useState<CrmUser[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const refreshUsers = async (orgId?: string) => {
        const organizationId = orgId || currentOrganization?.id;
        if (!organizationId) {
            setCrmUsers([]);
            return;
        }
        try {
            const data = await getCrmUsers(organizationId);
            setCrmUsers(data);
        } catch (error) {
            ErrorHandler.handle(error as Error, 'RefreshUsers');
            setCrmUsers([]);
        }
    };

    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);
            try {
                if (currentOrganization?.id) {
                    await refreshUsers(currentOrganization.id);
                } else {
                    setCrmUsers([]);
                }
            } finally {
                setIsLoading(false);
            }
        };
        loadData();
    }, [currentOrganization?.id]);

    const saveCrmUser = async (userToSave: CrmUser) => {
        try {
            const isNew = !userToSave.id || userToSave.id.startsWith('temp_');

            if (isNew) {
                await createUserUseCase.execute({
                    name: userToSave.name,
                    email: '', // CrmUser type doesn't have email, will need to handle this
                    role: userToSave.accessType === 'Administrador' ? 'admin' : 'agent'
                });
            } else {
                await updateUserUseCase.execute({
                    id: userToSave.id,
                    name: userToSave.name,
                    role: userToSave.accessType === 'Administrador' ? 'admin' : 'agent'
                });
            }

            await refreshUsers();
        } catch (error) {
            ErrorHandler.handle(error as Error, 'SaveUser');
        }
    };

    const deleteCrmUser = async (userId: string) => {
        try {
            await deleteUserUseCase.execute(userId);
            setCrmUsers(prev => prev.filter(u => u.id !== userId));
        } catch (error) {
            ErrorHandler.handle(error as Error, 'DeleteUser');
        }
    };

    const value = {
        crmUsers,
        isLoading,
        saveCrmUser,
        deleteCrmUser,
        refreshUsers
    };

    return (
        <UsersContext.Provider value={value}>
            {children}
        </UsersContext.Provider>
    );
};

export const useUsers = (): UsersContextType => {
    const context = useContext(UsersContext);
    if (!context) {
        throw new Error('useUsers must be used within a UsersProvider');
    }
    return context;
};
